import type { Deck, MediaElement, Page } from "@deckloom/core/schema";
import { walkElements } from "@deckloom/core/timeline";
import type { MediaCommand } from "./StepPlayer";

export interface MediaSyncState {
  elementId: string;
  /** ms into the media at `serverTime` */
  position: number;
  playing: boolean;
  serverTime: number;
}

export interface MediaControllerOptions {
  /** called when the user interacts with native controls (presenter) */
  onLocalEvent?: (elementId: string, action: "play" | "pause" | "seek" | "stop", positionMs: number) => void;
}

/**
 * Owns every <video>/<audio> on the mounted page. Applies timeline media effects, presenter
 * sync messages, trim/loop/fade rules, and the autoplay-policy fallbacks.
 */
export class MediaController {
  private els = new Map<string, { el: HTMLMediaElement; model: MediaElement; fadeTimer?: number }>();
  private unlocked = false;
  private suppress = new Set<string>();

  constructor(private readonly deck: Deck, private readonly page: Page, private readonly opts: MediaControllerOptions = {}) {}

  /** Register a media element rendered by MediaLayer. */
  attach(elementId: string, el: HTMLMediaElement): void {
    let model: MediaElement | undefined;
    for (const e of walkElements(this.page.elements)) if (e.id === elementId && e.kind === "media") model = e;
    if (!model) return;
    this.els.set(elementId, { el, model });
    el.volume = model.mute ? 0 : model.volume;
    el.muted = model.mute;
    el.loop = false; // loop is handled manually so trim is respected
    el.currentTime = model.trim.startAt / 1000;
    el.addEventListener("timeupdate", () => this.enforceTrim(elementId));
    el.addEventListener("ended", () => {
      if (model!.loop) {
        el.currentTime = model!.trim.startAt / 1000;
        void el.play().catch(() => undefined);
      }
    });
    el.addEventListener("play", () => this.emit(elementId, "play"));
    el.addEventListener("pause", () => { if (!el.ended) this.emit(elementId, "pause"); });
    el.addEventListener("seeked", () => this.emit(elementId, "seek"));
  }

  detach(elementId: string): void {
    this.els.delete(elementId);
  }

  private emit(elementId: string, action: "play" | "pause" | "seek"): void {
    if (this.suppress.has(elementId)) return;
    const entry = this.els.get(elementId);
    if (!entry) return;
    this.opts.onLocalEvent?.(elementId, action, Math.round(entry.el.currentTime * 1000));
  }

  /** Run a callback without echoing the resulting media events back as local events. */
  private silently(elementId: string, fn: () => void): void {
    this.suppress.add(elementId);
    try { fn(); } finally { setTimeout(() => this.suppress.delete(elementId), 50); }
  }

  private enforceTrim(elementId: string): void {
    const entry = this.els.get(elementId);
    if (!entry) return;
    const { el, model } = entry;
    const end = model.trim.endAt;
    if (end !== null && el.currentTime * 1000 >= end) {
      if (model.loop) el.currentTime = model.trim.startAt / 1000;
      else { el.pause(); el.currentTime = end / 1000; el.dispatchEvent(new Event("ended")); }
    }
    // fade out near the end
    if (model.fade.out > 0 && end !== null) {
      const remaining = end - el.currentTime * 1000;
      if (remaining < model.fade.out) el.volume = Math.max(0, (model.mute ? 0 : model.volume) * (remaining / model.fade.out));
    }
  }

  /** Apply a timeline media effect (fires on every client as the step plays). */
  apply(cmd: MediaCommand): void {
    const entry = this.els.get(cmd.elementId);
    if (!entry) return;
    const { el, model } = entry;
    this.silently(cmd.elementId, () => {
      switch (cmd.action) {
        case "play":
        case "resume":
          void this.safePlay(el, model);
          break;
        case "playFrom":
          el.currentTime = Math.max(model.trim.startAt, cmd.at ?? 0) / 1000;
          void this.safePlay(el, model);
          break;
        case "pause":
          el.pause();
          break;
        case "stop":
          el.pause();
          el.currentTime = model.trim.startAt / 1000;
          break;
        case "togglePause":
          if (el.paused) void this.safePlay(el, model); else el.pause();
          break;
      }
    });
  }

  /** Apply a presenter sync message. `serverNow` is the client's estimate of the server clock. */
  applySync(sync: MediaSyncState, serverNow: number): void {
    const entry = this.els.get(sync.elementId);
    if (!entry) return;
    const { el, model } = entry;
    this.silently(sync.elementId, () => {
      const expected = sync.playing ? sync.position + (serverNow - sync.serverTime) : sync.position;
      const drift = expected - el.currentTime * 1000;
      if (Math.abs(drift) > 400 || !sync.playing) {
        el.currentTime = expected / 1000;
        el.playbackRate = 1;
      } else if (Math.abs(drift) > 100) {
        el.playbackRate = drift > 0 ? 1.05 : 0.95;
        setTimeout(() => { el.playbackRate = 1; }, 3000);
      }
      if (sync.playing && el.paused) void this.safePlay(el, model);
      if (!sync.playing && !el.paused) el.pause();
    });
  }

  /** Positions of everything currently playing (presenter heartbeat). */
  snapshot(): { elementId: string; position: number; playing: boolean }[] {
    const out: { elementId: string; position: number; playing: boolean }[] = [];
    for (const [id, { el }] of this.els) {
      if (!el.paused) out.push({ elementId: id, position: Math.round(el.currentTime * 1000), playing: true });
    }
    return out;
  }

  /** Called from a user gesture: primes every media element so later programmatic play() is allowed. */
  async unlock(): Promise<void> {
    this.unlocked = true;
    for (const { el } of this.els.values()) {
      const wasMuted = el.muted;
      try {
        await el.play();
        el.pause();
        el.currentTime = Math.max(0, el.currentTime);
      } catch { /* still blocked; muted fallback will apply */ }
      el.muted = wasMuted;
    }
  }

  get isUnlocked(): boolean {
    return this.unlocked;
  }

  private async safePlay(el: HTMLMediaElement, model: MediaElement): Promise<void> {
    if (model.fade.in > 0) {
      const target = model.mute ? 0 : model.volume;
      el.volume = 0;
      const start = performance.now();
      const tick = () => {
        const f = Math.min(1, (performance.now() - start) / model.fade.in);
        el.volume = target * f;
        if (f < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
    try {
      await el.play();
    } catch (err) {
      if ((err as DOMException)?.name === "NotAllowedError") {
        el.muted = true;
        el.dispatchEvent(new CustomEvent("deckloom:muted-fallback", { bubbles: true }));
        try { await el.play(); } catch { /* give up */ }
      }
    }
  }

  stopAll(): void {
    for (const [id, { el }] of this.els) this.silently(id, () => el.pause());
  }

  destroy(): void {
    this.stopAll();
    this.els.clear();
  }

  /** Media elements that should live at deck level (play across slides / narration). */
  static deckLevelMedia(deck: Deck): MediaElement[] {
    const out: MediaElement[] = [];
    for (const page of deck.pages) for (const e of walkElements(page.elements)) if (e.kind === "media" && (e.playAcrossSlides > 1 || e.isNarration)) out.push(e);
    return out;
  }
}
